import React from 'react';
import { Tag, DollarSign, BarChart3, Clock, Layers, Sunrise, History, ChevronsUp, ChartNoAxesCombined, Sunset, Target, ChevronsDown } from 'lucide-react';
import { Stats, Trade, OrderBookEntry } from '../types';
import { toEAT } from '../utils/time';

export function OverviewCards({
  selectedSymbol,
  labelForSecId,
  currentPrice,
  lastUpdateTime,
  stats,
  recentTrades,
  orderBook
}: {
  selectedSymbol: string;
  labelForSecId: (s: string) => string;
  currentPrice: number;
  lastUpdateTime: string;
  stats: Stats;
  recentTrades: Trade[];
  orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] };
}) {
  const lastTrade = recentTrades.find(t => t.symbol ? t.symbol.toUpperCase() === selectedSymbol.toUpperCase() : true);
  const lastUpdateDisplay = (() => {
    if (!lastUpdateTime) return 'N/A';
    const ts = Date.parse(lastUpdateTime);
    if (!Number.isFinite(ts)) return lastUpdateTime;
    const ageSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    const hhmm = toEAT(new Date(ts).toISOString());
    const ageStr = ageSec < 60 ? `${ageSec}s ago` : ageSec < 3600 ? `${(ageSec/60).toFixed(1)}m ago` : `${(ageSec/3600).toFixed(1)}h ago`;
    return `${hhmm} (${ageStr})`;
  })();

  return (
    <div className="overview-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
      <Card icon={<Tag size={16} color="#3b82f6" />} title="Symbol">
        <div style={{ fontSize: 20, fontWeight: 600 }}>
          {labelForSecId(selectedSymbol) || 'Unknown'}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{selectedSymbol}</div>
      </Card>

      <Card icon={<DollarSign size={16} color="#10b981" />} title="Current Price">
        <div style={{ fontSize: 20, fontWeight: 600 }}>{currentPrice.toFixed(2)}</div>
        <div style={{ display: 'flex', gap: 4, marginTop: 4, fontSize: 11, color: '#ef4444' }}>-5.00 (-0.41%)</div>
      </Card>

      <Card icon={<BarChart3 size={16} color="#3b82f6" />} title="Volume">
        <div style={{ fontSize: 20, fontWeight: 600 }}>
          {(stats.volume ?? recentTrades.reduce((s, t) => s + t.size, 0)).toLocaleString()}
        </div>
      </Card>

      <Card icon={<Clock size={16} color="#f59e0b" />} title="Last Update">
        <div style={{ fontSize: 20, fontWeight: 600 }}>{toEAT(lastUpdateTime)}</div>
        {lastTrade && (
          <div style={{ fontSize: 11, color: '#ffffffff', marginTop: 4 }}>
            Last Trade: {lastTrade.side.toUpperCase()} {lastTrade.size.toLocaleString()} @ {lastTrade.price.toFixed(2)}
          </div>
        )}
      </Card>

      <Card icon={<Layers size={16} color="#10b981" />} title="Book Depth">
        <div style={{ fontSize: 20, fontWeight: 600 }}>
          {orderBook.bids.length + orderBook.asks.length}
        </div>
      </Card>

      <Card icon={<Sunrise size={16} color="#f0f005" />} title="Open">
        <div style={{ fontSize: 20, fontWeight: 600 }}>{stats.open != null ? stats.open.toFixed(2) : '—'}</div>
      </Card>

      <Card icon={<History size={16} color="#e47c1c" />} title="Prev Close">
        <div style={{ fontSize: 20, fontWeight: 600 }}>{stats.prevClose != null ? stats.prevClose.toFixed(2) : '—'}</div>
      </Card>

      <Card icon={<ChevronsUp size={16} color="#17d64d" />} title="High">
        <div style={{ fontSize: 20, fontWeight: 600 }}>{stats.high != null ? stats.high.toFixed(2) : '—'}</div>
      </Card>

      <Card icon={<ChartNoAxesCombined size={16} color="#3b75c7" />} title="VWAP">
        <div style={{ fontSize: 20, fontWeight: 600 }}>{stats.vwap != null ? stats.vwap.toFixed(2) : '—'}</div>
      </Card>

      <Card icon={<Sunset size={16} color="#e29032" />} title="Close">
        <div style={{ fontSize: 20, fontWeight: 600 }}>{stats.close != null ? stats.close.toFixed(2) : '—'}</div>
      </Card>

      <Card icon={<Target size={16} color="#94a3b8" />} title="Last">
        <div style={{ fontSize: 20, fontWeight: 600 }}>{stats.last != null ? stats.last.toFixed(2) : '—'}</div>
        {stats.lastTime && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{stats.lastTime}</div>}
      </Card>

      <Card icon={<ChevronsDown size={16} color="#db2828" />} title="Low">
        <div style={{ fontSize: 20, fontWeight: 600 }}>{stats.low != null ? stats.low.toFixed(2) : '—'}</div>
      </Card>
    </div>
  );
}

function Card({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#1e293b', padding: 16, borderRadius: 8, border: '1px solid #334155' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        {icon}
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}
